import {
  usePricelistProductDeleteMutation,
  usePricelistProductListQuery,
} from "@/generated/graphql";
import type { PaginationState } from "@tanstack/react-table";
import { useCallback, useMemo, useState } from "react";

export const usePricelistProductList = (
  {
    page,
    pageSize,
    withCount,
    pricelistID,
    pricelistPeriodID,
  }: {
    page: number;
    pageSize: number;
    withCount: boolean;
    pricelistID?: string;
    pricelistPeriodID?: string;
  } = {
    page: 1,
    pageSize: 10,
    withCount: true,
  },
) => {
  const [paginationState, setPaginationState] = useState<PaginationState>({
    pageIndex: page - 1,
    pageSize: pageSize,
  });
  const [filtersState, setFiltersState] = useState<Record<string, unknown>>({});
  const [, deletePricelistProduct] = usePricelistProductDeleteMutation();

  const options: Parameters<typeof usePricelistProductListQuery>[0] = useMemo(
    () => ({
      variables: {
        qop: {
          pagination: {
            page: paginationState.pageIndex + 1,
            pageSize: paginationState.pageSize,
            withCount,
          },
          filters: {
            ...filtersState,
            pricelistID,
            pricelistPeriodID,
            // Consignment products are priced from the vendor consignment price, so their pricelist rows are never shown.
            isConsignment: false,
          },
        },
      },
      requestPolicy: "network-only",
      context: {
        suspense: false,
      },
    }),
    [
      paginationState.pageIndex,
      paginationState.pageSize,
      withCount,
      filtersState,
      pricelistID,
      pricelistPeriodID
    ],
  );

  const [{ data, error, fetching }, fetch] = usePricelistProductListQuery(options);

  const load = useCallback(() => {
    fetch();
  }, [fetch]);

  const setPagination = useCallback((paginationState: PaginationState) => {
    setPaginationState(paginationState);
  }, []);

  const setFilters = useCallback((filtersState: Record<string, unknown>) => {
    setFiltersState(filtersState);
		setPaginationState(prev => ({ ...prev, pageIndex: 0 }));
  }, []);

  return {
    data,
    fetching,
    error,
    load,
    paginationState,
    setPagination,
    filtersState,
    setFilters,
    deletePricelistProduct,
  };
};
